import { getLoginServiceData, getProductsData, getSelectedProductValues, isOnlyNoReturnComplaintsSelected, isServiceFlowActive } from "./appCore";
import { ROUTES } from "../constants";
import { isNull, isNumber } from "lodash"; //

export const SHIPMENT_SOLUTION_TYPES = {
    PICKUP: {
        key: "PICKUP",
    },
    MANUAL: {
        key: "MANUAL",
    },
    PRINT_LABEL: {
        key: "PRINT_LABEL",
    },
    RETURN_IN_STORE: {
        key: "RETURN_IN_STORE",
    },
    RETURN_SLIP: {
        key: "RETURN_SLIP",
    },
    PRINT_LABEL_MANUAL_UPLOAD: {
        key: "PRINT_LABEL_MANUAL_UPLOAD",
    },
    LOCKER: {
        key: "LOCKER",
    },
    NO_RETURN_NEEDED: {
        key: "NO_RETURN_NEEDED",
    },
    REGISTER_WITHDRAWAL: {
        key: "REGISTER_WITHDRAWAL",
    },
};

export function getSelectedShipmentSolution(appProps) {
    return isOnlyNoReturnComplaintsSelected(appProps) ? { type: SHIPMENT_SOLUTION_TYPES.NO_RETURN_NEEDED.key } : getAvailableShipmentSolutions(appProps)[getSelectedShipmentSolutionIndex(appProps)];
}

export function onlyOneSimpleShipmentSolutionAvailable(appProps) {
    const solutions = getAvailableShipmentSolutions(appProps);
    return solutions.length === 1 && !shipmentMethodRequiresMultipleSelection(solutions[0]);
}

export function getAvailableShipmentSolutions(appProps) {
    return getLoginServiceData(appProps)?.shipmentSolutions || [];
}

export function getSelectedShipmentSolutionData(appProps) {
    return appProps?.selectedShipmentSolutionData;
}

export function getSelectedShipmentSolutionIndex(appProps) {
    return getSelectedShipmentSolutionData(appProps)?.index;
}

export function getSelectedShipmentSolutionMethodOption(appProps) {
    return getAvailableShipmentSolutions(appProps)?.[getSelectedShipmentSolutionIndex(appProps)];
}

export function getSelectedShipmentSolutionDateIndex(appProps) {
    return getSelectedShipmentSolutionData(appProps)?.dateIndex;
}

export function isAnyShipmentSolutionSelected(appProps) {
    return isNumber(getSelectedShipmentSolutionData(appProps));
}

export function isPickupDateSelectionAllowed(appProps) {
    return getLoginServiceData(appProps)?.pickupDateSelectionAllowed;
}

export function shipmentMethodDateOnly(appProps) {
    return isPickupDateSelectionAllowed(appProps) && getAvailableShipmentSolutions(appProps).length === 0;
}

export function isResaleActive(appProps) {
    return !!getLoginServiceData(appProps)?.resaleActive;
}

export function isShipmentMethodPageEnabled(appProps) {
    const solutions = getAvailableShipmentSolutions(appProps);
    if (solutions.length === 0) {
        return false;
    }

    if (solutions.length === 1 && solutions[0]?.type === SHIPMENT_SOLUTION_TYPES.REGISTER_WITHDRAWAL.key) {
        return false;
    }

    if (isOnlyNoReturnComplaintsSelected(appProps)) {
        return false;
    }

    if (getSelectedProductValues(appProps)?.length === 0) {
        return true;
    }

    if (isPickupDateSelectionAllowed(appProps)) {
        return true;
    }

    return true;
}

export const FLOW_STEP = {
    ITEMS: "items",
    SHIPPING: "shipping",
    CONFIRM: "confirm",
};

// The steps this flow actually visits, in order. A service flow never selects items -- the
// whole order is sent in -- so it has no item step to render, number or navigate to. The keys
// double as the stepper's locale keys.
export function getFlowSteps(appProps) {
    const steps = [];
    if (!isServiceFlowActive(appProps)) {
        steps.push(FLOW_STEP.ITEMS);
    }
    if (isShipmentMethodPageEnabled(appProps)) {
        steps.push(FLOW_STEP.SHIPPING);
    }
    steps.push(FLOW_STEP.CONFIRM);
    return steps;
}

// 1-based position of a step in this flow, or 0 when the flow does not have it.
export function getFlowStepNumber(appProps, step) {
    return getFlowSteps(appProps).indexOf(step) + 1;
}

// Whether anything on the order can still be sent in. The backend marks an item that has
// already been returned as returnable === false but still includes it, so an order where
// everything has been returned logs in fine with nothing left to select.
//
// A standard return shows that plainly -- the items page renders them all disabled. A service
// flow has no items page, so without this it would walk the customer through the shipping step
// and refuse them at Confirm, where the backend rejects an empty product list.
export function hasReturnableProducts(appProps) {
    return getProductsData(appProps).some((product) => product?.returnable);
}

// Where the shopper lands once they are logged in. A service flow has no items to pick,
// so it goes straight to the shipping step -- and has to fall through to confirmation
// when there is no shipping step, which for a standard return is ReturnPage's job.
export function getPostLoginPathname(appProps) {
    if (!isServiceFlowActive(appProps)) {
        return ROUTES.RETURN_PAGE.pathname;
    }
    return isShipmentMethodPageEnabled(appProps) ? ROUTES.SHIPMENT_METHOD_PAGE.pathname : ROUTES.CONFIRMATION_PAGE.pathname;
}

export function isShipmentMethodInputsValid(appProps) {
    const selectedShipmentMethod = getSelectedShipmentSolutionMethodOption(appProps);
    if (isShipmentMethodWithOptions(selectedShipmentMethod)) {
        return getSelectedShipmentSolutionData(appProps)?.foreignOptionId;
    }

    return (shipmentMethodDateOnly(appProps) && !isNull(getSelectedShipmentSolutionDateIndex(appProps))) || (!shipmentMethodDateOnly(appProps) && !!getSelectedShipmentSolutionData(appProps));
}

export function isDateSelectorShipmentMethod(shipmentMethod) {
    return shipmentMethod?.type === SHIPMENT_SOLUTION_TYPES.PICKUP.key;
}

export function getShipmentMethodOptions(shipmentMethod) {
    return shipmentMethod?.availableOptions?.options;
}

export function isShipmentMethodWithOptions(shipmentMethod) {
    return getShipmentMethodOptions(shipmentMethod)?.length > 0;
}

export function shipmentMethodRequiresMultipleSelection(shipmentMethod) {
    return isDateSelectorShipmentMethod(shipmentMethod) || isShipmentMethodWithOptions(shipmentMethod);
}

export function getShipmentCarrierData(shipmentMethod) {
    return shipmentMethod?.carrierData;
}

export function getCarrierOrSubCarrierData(shipmentMethod) {
    return shipmentMethod?.subCarrierData || shipmentMethod?.carrierData;
}

export function getShipmentSolutionData(shipmentMethod) {
    return SHIPMENT_SOLUTION_TYPES[shipmentMethod?.type];
}

export function getShipmentCarrier(shipmentMethod) {
    return shipmentMethod?.carrier;
}

export function getShipmentOptionData(shipmentMethod, { appProps }) {
    function getShipmentMethodSolutionTitle(shipmentSolutionType, carrierName) {
        return (
            appProps?.getLocaleText(`shipment-method-solution-title.${shipmentSolutionType}.${carrierName}`) ||
            appProps?.getLocaleText(`shipment-method-solution-title.${shipmentSolutionType}.default`) ||
            appProps?.getLocaleText(`shipment-method-solution-title.${shipmentSolutionType}`)
        );
    }

    function getShipmentMethodSolutionTitleCarrier(shipmentMethod) {
        return `shipment-method-solution-title-carrier.${getShipmentCarrier(shipmentMethod)}`;
    }

    function getShipmentMethodSolutionSubTitleCarrier(shipmentMethod) {
        return `shipment-method-solution-subtitle-carrier.${getShipmentCarrier(shipmentMethod)}`;
    }

    if (shipmentMethod?.type === SHIPMENT_SOLUTION_TYPES.MANUAL.key) {
        return {
            title: appProps?.getLocaleText("manual-shipping-option"),
            icon: "/icons/delivery/manual.png",
        };
    }
    if (shipmentMethod?.type === SHIPMENT_SOLUTION_TYPES.PRINT_LABEL_MANUAL_UPLOAD.key) {
        return {
            title: appProps?.getLocaleText("manual-upload-shipping-option"),
            icon: "/icons/delivery/return_slip.webp",
        };
    }
    if (shipmentMethod?.type === SHIPMENT_SOLUTION_TYPES.RETURN_SLIP.key) {
        return {
            title: appProps?.getLocaleText("return-slip-shipping-option"),
            icon: "/icons/delivery/return_slip.webp",
        };
    }
    if (shipmentMethod?.type === SHIPMENT_SOLUTION_TYPES.RETURN_IN_STORE.key) {
        return {
            title: appProps?.getLocaleText("shipment-method-solution-title." + shipmentMethod?.type),
            subTitle: appProps?.getLocaleText("shipment-method-solution-subtitle." + shipmentMethod?.type),
            icon: "/icons/delivery/in_store.png",
        };
    }

    const shipmentCarrierData = getShipmentCarrierData(shipmentMethod);
    const shipmentSolutionData = getShipmentSolutionData(shipmentMethod);

    const shipmentSolutionName = getShipmentMethodSolutionTitle(shipmentSolutionData?.key, shipmentMethod?.carrier) || shipmentSolutionData?.key;

    const shipmentCarrierOverrideTitle = appProps?.getLocaleText(getShipmentMethodSolutionTitleCarrier(shipmentMethod));
    const shipmentCarrierOverrideSubTitle = appProps?.getLocaleText(getShipmentMethodSolutionSubTitleCarrier(shipmentMethod));

    const title = shipmentCarrierOverrideTitle || (shipmentCarrierData?.displayName ? shipmentSolutionName : null);
    const subTitle = shipmentCarrierOverrideSubTitle || shipmentCarrierData?.displayName || shipmentSolutionName;

    return {
        title: title || subTitle,
        subTitle: title ? subTitle : null,
    };
}

export function getCarrierColor(carrierData) {
    return carrierData?.color || "transparent";
}
